import React, { useMemo, useState } from 'react';
import { ChevronDown, Loader2, Undo2 } from 'lucide-react';
import { MuseBulletsCard } from './LiveSecretary';

// Où en est le quoi de neuf d'un projet de la réunion : ce qui est enregistré (Nexus ou secrétaire) fait foi, sinon
// les puces du direct. « pending » tant que Nexus n'a pas fait sa passe finale sur ce projet quitté.
export const projectRecapState = ({ projectId, live, review }) => {
  const note = (live?.project_notes || []).find((entry) => String(entry?.project_id) === String(projectId)) || null;
  const saved = Array.isArray(review?.news) ? review.news : [];
  const bullets = saved.length ? saved : (note?.bullets || []);
  const bySecretary = review?.news_source === 'secretaire';
  const listening = Boolean(live?.active) && !bySecretary && !note?.locked;
  return {
    note,
    bullets,
    source: review?.news_source,
    pending: listening && Boolean(note) && !note.final,
    updating: Boolean(note?.updating),
    name: note?.project_name || review?.project_name || null,
  };
};

// Ordre de passage : celui du direct (tient aux rechargements), sinon les projets choisis depuis l'ouverture de
// la page. Le plus récent est à la fin.
export const reviewOrderOf = (liveOrder, visited) => {
  const fromLive = (liveOrder || []).map(String);
  return fromLive.length ? fromLive : (visited || []).map(String);
};

const RecapCard = ({ projectId, label, name, state, live, userIsSecretary, onSave, onSelect }) => (
  <MuseBulletsCard
    key={projectId}
    variant="recap"
    title={label}
    heading={name}
    bullets={state.bullets}
    source={state.source}
    listening={false}
    updating={state.updating}
    pendingLabel={state.pending ? 'Nexus finit les points clés…' : null}
    userIsSecretary={userIsSecretary}
    onSave={onSave ? (items) => onSave(projectId, items) : null}
    emptyListening="Les points clés arrivent dans quelques secondes."
    emptyIdle="Aucun point clé noté pour ce projet."
    lockHint=""
    lastError={live?.lastError}
    extra={userIsSecretary && onSelect ? (
      <button
        type="button"
        onClick={() => onSelect(projectId)}
        title="Revenir sur ce projet"
        className="flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-slate-500 hover:bg-white hover:text-slate-700"
      >
        <Undo2 size={12} /> Y revenir
      </button>
    ) : null}
  />
);

// Rappel des projets déjà passés en revue, au-dessus du projet en cours : le précédent en entier (ses points clés
// arrivent quelques secondes après le passage au suivant), les autres en pastilles à déplier.
const ReviewedProjects = ({
  order, currentProjectId, projectsById, live, reviewByProjectId, userIsSecretary, onSave, onSelect,
}) => {
  const [openId, setOpenId] = useState(null);
  const previous = useMemo(
    () => (order || []).filter((id) => String(id) !== String(currentProjectId)).reverse(),
    [order, currentProjectId]
  );
  if (!previous.length) return null;

  const describe = (projectId) => {
    const review = reviewByProjectId[String(projectId)]?.current || null;
    const state = projectRecapState({ projectId, live, review });
    const project = projectsById[String(projectId)];
    return { state, name: project?.name || state.name || `Projet #${projectId}` };
  };
  const [last, ...earlier] = previous;
  const lastInfo = describe(last);
  const opened = earlier.includes(String(openId)) ? describe(openId) : null;

  return (
    <section className="space-y-2" aria-label="Projets déjà passés en revue">
      <RecapCard
        projectId={last}
        label="Juste avant"
        name={lastInfo.name}
        state={lastInfo.state}
        live={live}
        userIsSecretary={userIsSecretary}
        onSave={onSave}
        onSelect={onSelect}
      />
      {earlier.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Plus tôt</span>
            {earlier.map((projectId) => {
              const { state, name } = describe(projectId);
              const isOpen = String(openId) === String(projectId);
              return (
                <button
                  key={projectId}
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : projectId)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${isOpen
                    ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-indigo-200'}`}
                >
                  <span className="max-w-[12rem] truncate">{name}</span>
                  {state.pending ? (
                    <Loader2 size={11} className="animate-spin text-indigo-400" />
                  ) : (
                    <span className="rounded-full bg-white px-1.5 text-[10px] font-semibold text-slate-400">
                      {state.bullets.length}
                    </span>
                  )}
                  <ChevronDown size={11} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
              );
            })}
          </div>
          {opened && (
            <RecapCard
              projectId={openId}
              label="Plus tôt"
              name={opened.name}
              state={opened.state}
              live={live}
              userIsSecretary={userIsSecretary}
              onSave={onSave}
              onSelect={onSelect}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default ReviewedProjects;
